import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {lerpGreenToRedViaOrange, scaleNumber} from "../../helpers/scaling";
import {clearDiv} from "../../helpers/builders";
import {formatTimeRange, enableTooltips, disposeTooltips, unixToISO} from "../../helpers/tooltips";
import {buildHeatmapIssues} from "../../helpers/heatmap_explain";
import {getTopLevelItems, parseShaperChildrenBlob} from "../../helpers/shaper_children";

export class ShaperRetransmitHeatmap extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.explainIssues = null;
    }

    title() {
        return "Retransmits Heatmap";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Shaper Heatmap — TCP Retransmits", issues: this.explainIssues };
    }

    channelRequests() {
        return [ "SiteShaperPage" ];
    }

    buildContainer() {
        let base = super.buildContainer();
        base.style.overflow = "auto";
        let content = document.createElement("div");
        content.style.width = "100%";
        content.id = "rxmitHeatmap_" + this.slot;
        content.classList.add("text-center", "text-muted", "small", "lqos-heatmap-fill");
        this.contentId = content.id;
        base.appendChild(content);
        return base;
    }

    setup() {
        super.setup();
        $("#" + this.contentId).html("<i class='fa fa-spinner fa-spin'></i> Loading...");
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_children") return;

        // Display Render Status
        // TODO: Promote this to a base class
        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }

        const parsed = parseShaperChildrenBlob(msg.Data.blob);
        window.isFlatNetwork = parsed.isFlat;
        const dataset = getTopLevelItems(parsed);
        const sites = dataset.topLevel || {};
        const isCircuit = parsed.isFlat;
        let n_rows = 0;
        for (const [siteHash, value] of Object.entries(sites)) {
            n_rows = Math.max(n_rows, (value.body.ticks || []).length);
        }
        const DESIRED_COLUMNS = 10;
        let group_size = Math.max(1, Math.ceil(n_rows / DESIRED_COLUMNS));
        let result = [];
        for (const [siteHash, value] of Object.entries(sites)) {
            let site = {
                name: value.header.name,
                hash: siteHash,
                type: isCircuit ? "circuit" : "site",
                vals: [],
                explainVals: [],
                ticks: value.body.ticks,
                ranges: [],
            };
            const downSeries = (isCircuit ? value.body.tcp_retransmits_down_percent : value.body.retransmits_down_percent) || [];
            const upSeries = (isCircuit ? value.body.tcp_retransmits_up_percent : value.body.retransmits_up_percent) || [];
            for (let i=0; i<n_rows; i+=group_size) {
                let bucketDisplay = [];
                let bucketWorst = [];
                for (let j=0; j<group_size; j++) {
                    const idx = i + j;
                    const downVal = idx < downSeries.length ? downSeries[idx] : null;
                    const upVal = idx < upSeries.length ? upSeries[idx] : null;
                    const sampleVals = [];
                    if (Number.isFinite(downVal)) {
                        sampleVals.push(downVal / 100);
                    }
                    if (Number.isFinite(upVal)) {
                        sampleVals.push(upVal / 100);
                    }
                    if (sampleVals.length > 0) {
                        const avg = sampleVals.reduce((a, b) => a + b, 0) / sampleVals.length;
                        const worst = Math.max(...sampleVals);
                        bucketDisplay.push(avg);
                        bucketWorst.push(worst);
                    }
                }
                const med = medianFinite(bucketDisplay);
                const worstMed = medianFinite(bucketWorst);
                site.vals.push(med);
                site.explainVals.push(worstMed);
                if (site.ticks && site.ticks.length > 0 && i < site.ticks.length) {
                    const startIdx = i;
                    const endIdx = Math.min(i + group_size - 1, site.ticks.length - 1);
                    site.ranges.push({ start: site.ticks[startIdx], end: site.ticks[endIdx] });
                } else {
                    site.ranges.push(null);
                }
            }
            if (site.vals.some((v) => v !== null)) {
                result.push(site);
            }
        }

        // Clear the div
        let target = document.getElementById(this.contentId);
        disposeTooltips(target);
        clearDiv(target);

        // Build the table
        let table = document.createElement("table");
        table.classList.add("small", "muted", "lqos-heatmap-table");

        result.forEach((row) => {
            let tr = document.createElement("tr");
            tr.addEventListener("mouseenter", () => tr.classList.add("lqos-heatmap-row-hover"));
            tr.addEventListener("mouseleave", () => tr.classList.remove("lqos-heatmap-row-hover"));
            let title = document.createElement("td");
            title.classList.add("lqos-heatmap-label");
            title.title = row.name;
            let name = row.name;
            if (name.length > 12) {
                name = name.substring(0, 12) + "...";
            }
            let link = document.createElement("a");
            if (window.isFlatNetwork) {
                link.href = `shaper_circuit.html?circuit=${row.hash}&nodeId=${window.nodeId}`;
            } else {
                link.href = `shaper_site.html?site=${row.hash}&nodeId=${window.nodeId}`;
            }
            link.appendChild(document.createTextNode(name));
            link.classList.add("redactable");
            link.title = row.name;
            title.appendChild(link);
            title.style.textAlign = "right";
            tr.appendChild(title);

            for (let i=0; i<row.vals.length; i++) {
                let cell = row.vals[i];
                let cellElement = document.createElement("td");
                cellElement.classList.add("lqos-heatmap-cell");
                let rtt = cell;
                
                // Add Bootstrap tooltip with time range and value
                const range = Array.isArray(row.ranges) ? row.ranges[i] : null;
                let rangeStart = null;
                let rangeEnd = null;
                if (range && Number.isFinite(range.start) && Number.isFinite(range.end)) {
                    rangeStart = range.start;
                    rangeEnd = range.end;
                } else if (row.ticks && row.ticks.length > 0) {
                    const startIdx = Math.min(i * group_size, row.ticks.length - 1);
                    const endIdx = Math.min(startIdx + group_size - 1, row.ticks.length - 1);
                    rangeStart = row.ticks[startIdx];
                    rangeEnd = row.ticks[endIdx];
                }
                if (rangeStart !== null && rangeEnd !== null) {
                    const timeRange = formatTimeRange(rangeStart, rangeEnd);
                    const valueStr = rtt !== null ? `${rtt.toFixed(1)}%` : 'No Data';
                    cellElement.setAttribute('data-bs-toggle', 'tooltip');
                    cellElement.setAttribute('data-bs-title', `${timeRange}\nMedian Retransmits: ${valueStr}\n\nClick to zoom to this time period`);
                    cellElement.style.cursor = "pointer";
                    
                    // Add click handler to set time period
                    cellElement.onclick = () => {
                        if (window.timePicker) {
                            const start = unixToISO(rangeStart);
                            const end = unixToISO(rangeEnd + 60); // +1 minute buffer
                            window.timePicker.absoluteTime(start, end);
                        }
                    };
                }
                
                if (rtt === null) {
                    cellElement.classList.add("lqos-heatmap-cell--empty");
                    cellElement.style.background = "#444";
                    cellElement.appendChild(document.createTextNode("-"));
                } else {
                    cellElement.appendChild(document.createTextNode(rtt.toFixed(1)));
                    cellElement.style.background = lerpGreenToRedViaOrange(10 - rtt, 10);
                    cellElement.style.color = "black";
                }
                tr.appendChild(cellElement);
            }
            table.appendChild(tr);
        });

        const rxmitIssues = buildHeatmapIssues({
            rows: result,
            metricLabel: "TCP retransmits",
            maxValue: 10,
            higherIsWorse: true,
            topN: 1,
            formatValue: (v) => `${v.toFixed(1)}%`,
            includeRowName: true,
            valueSelector: (row) => row.explainVals,
            severityDowngrade: 0,
        });

        // Retransmits are noisy; only report when "red", and treat as informational.
        this.explainIssues = rxmitIssues
            .filter((issue) => issue && issue.severity === "red")
            .map((issue) => ({ ...issue, severity: "info" }));

        // Add it
        target.appendChild(table);
        
        // Initialize Bootstrap tooltips
        enableTooltips(target);

        this.rendered = true;
    }
}

function medianFinite(values) {
    const data = values.filter((v) => Number.isFinite(v)).slice().sort((a, b) => a - b);
    if (data.length === 0) return null;
    const mid = Math.floor(data.length / 2);
    if (data.length % 2 === 1) {
        return data[mid];
    }
    return (data[mid - 1] + data[mid]) / 2;
}
